import { BaseLayouter } from './BaseLayouter';
import { Vector3 } from '../../types/Vector3';
import { LayoutData, SpiralOrientationMode, SpiralAxisType } from '../LayoutData';
import { AlignmentMode } from '../../field/FieldTypes';
import { UpVectorMode } from '../../field/FieldTypes';
import { Quaternion } from '../../types/Quaternion';
import * as THREE from 'three';

export class SpiralLayouter extends BaseLayouter {
    public readonly defaultRadius: number = 3.0;
    public readonly defaultHeight: number = 10.0;
    public readonly defaultTurns: number = 2.0;
    public readonly defaultSpacing: number = 1.0;
    
    constructor() {
        super(
            'spiral',
            'Arranges objects in a spiral pattern with adaptive spacing and configurable axis direction',
            {
                spiralRadius: 3.0,
                spiralHeight: 10.0,
                spiralTurns: 2.0,
                spiralOrientationMode: SpiralOrientationMode.Fixed,
                spiralAxis: SpiralAxisType.Y,
                spiralFixedSpacing: 1.0,
                rotationStep: 0,
                alignmentMode: AlignmentMode.Aligned,
                spiralCenterLayout: false
            }
        );
    }
    
    calculatePosition(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const baseHeight = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        const axis = this.getParameter(layoutData, 'spiralAxis', SpiralAxisType.Y);
        const centerLayout = this.getParameter(layoutData, 'spiralCenterLayout', false);
        
        // Calculate spiral parameters based on object count and spacing (all objects)
        const adjustedTotal = totalCount;
        const totalLength = adjustedTotal * spacing;
        const circumferencePerTurn = 2 * Math.PI * baseRadius;
        
        // Adjust turns based on desired spacing and total length
        const adjustedTurns = Math.max(0.5, totalLength / circumferencePerTurn);
        const adjustedHeight = totalLength / adjustedTurns;
        
        // Use the adjusted parameters or fall back to user-specified ones
        const finalTurns = spacing > 0 ? adjustedTurns : baseTurns;
        const finalHeight = spacing > 0 ? adjustedHeight : baseHeight;
        
        const adjustedIndex = index;
        let t = this.calculateProgress(adjustedIndex, adjustedTotal - 1); // 0 to 1
        
        // Apply centering if enabled
        if (centerLayout) {
            // Shift t so that the center of the spiral is at origin
            t = t - 0.5;
        }
        
        const angle = t * finalTurns * 2 * Math.PI;
        
        // Calculate base spiral position (along Y-axis by default)
        let spiralRadius, spiralX, spiralY, spiralZ;
        
        // Normal spiral calculation for all objects (no special case for index 0)
        spiralRadius = baseRadius * (1 - t * 0.1); // Slight taper
        spiralX = Math.cos(angle) * spiralRadius;
        spiralY = t * finalHeight;
        spiralZ = Math.sin(angle) * spiralRadius;
        
        // Transform based on axis direction
        return this.transformByAxis(spiralX, spiralY, spiralZ, axis);
    }
    
    calculateOrientation(index: number, totalCount: number, layoutData: LayoutData): Vector3 {
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        // Calculate parameter t for this object on the spiral
        const t = index / totalCount; // Parameter from 0 to 1
        
        // ============ PROVEN 1D ORIENTATION LOGIC FROM WORKING DEMO ============
        
        // 1. Calculate tangent vector (curve movement direction) using numerical differentiation
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = this.getSpiralPointAt(t1, layoutData);
        const p2 = this.getSpiralPointAt(t2, layoutData);
        const tangent = new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
        
        // 2. Calculate normal vector (pointing toward spiral center for consistent orientation)
        // For spirals, we use a geometric approach instead of second derivative to avoid flipping
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        
        // Calculate adjusted turns (same logic as in position calculation)
        const adjustedTotal = totalCount;
        const totalLength = adjustedTotal * spacing;
        const circumferencePerTurn = 2 * Math.PI * baseRadius;
        const adjustedTurns = spacing > 0 ? Math.max(0.5, totalLength / circumferencePerTurn) : baseTurns;
        const finalTurns = spacing > 0 ? adjustedTurns : baseTurns;
        
        // Calculate current angle using the same logic as position calculation
        const angle = t * finalTurns * 2 * Math.PI;
        
        // Normal always points toward spiral center (radially inward) - this ensures consistent orientation
        let normal = new Vector3(-Math.cos(angle), 0, -Math.sin(angle));
        normal.normalize();
        
        // 3. Calculate binormal vector (perpendicular to spiral plane)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        // 4. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            // ALIGNED: Object follows the curve direction (uses normal - curvature direction)
            desiredForward = normal.clone();
        } else {
            // PERPENDICULAR: Object is perpendicular to curve (uses tangent - movement direction)
            desiredForward = tangent.clone();
        }
        
        // 5. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
        } else {
            // PATH UP: Use binormal (perpendicular to spiral plane), negated for correct orientation
            desiredUp = binormal.clone().negate();
        }
        
        // 6. Build orthogonal coordinate system using proven logic
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism (degenerative case)
        const dot = Math.abs(desiredForward.dot(desiredUp));
        if (dot > 0.99) {
            if (upVectorMode === UpVectorMode.WorldUp) {
                // Forward is parallel to world up - use tangent direction as fallback
                desiredForward = tangent.clone().normalize();
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up must stay (0,1,0), calculate right and forward accordingly
            finalUp = new Vector3(0, 1, 0);
            
            // Check if desired forward is parallel to world up
            const parallelThreshold = 0.99;
            const forwardUpDot = Math.abs(desiredForward.dot(finalUp));
            
            if (forwardUpDot > parallelThreshold) {
                // Forward is parallel to world up - use tangent direction as forward
                finalForward = tangent.clone().normalize();
                finalRight = new Vector3().crossVectors(finalUp, finalForward);
                finalRight.normalize();
            } else {
                // Normal case: forward is not parallel to up
                finalRight = new Vector3().crossVectors(finalUp, desiredForward);
                finalRight.normalize();
                
                // Recalculate forward to ensure perfect orthogonality
                finalForward = new Vector3().crossVectors(finalRight, finalUp);
                finalForward.normalize();
            }
        } else {
            // PATH UP: Use the normal approach with binormal as up
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Calculate right vector: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Recalculate up to ensure orthogonality: up = forward × right
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        // 7. Convert orthogonal coordinate system to Euler angles (degrees)
        // Create rotation matrix from the orthogonal basis vectors
        const matrix = new THREE.Matrix4();
        matrix.makeBasis(
            new THREE.Vector3(finalRight.x, finalRight.y, finalRight.z),
            new THREE.Vector3(finalUp.x, finalUp.y, finalUp.z),
            new THREE.Vector3(finalForward.x, finalForward.y, finalForward.z)
        );
        
        // Extract Euler angles from rotation matrix
        const euler = new THREE.Euler();
        euler.setFromRotationMatrix(matrix, 'XYZ');
        
        // Convert to degrees and return as Vector3
        return new Vector3(
            euler.x * 180 / Math.PI,
            euler.y * 180 / Math.PI,
            euler.z * 180 / Math.PI
        );
    }
    
    /**
     * Calculate tangent vector along spiral curve
     */
    private calculateSpiralTangent(t: number, layoutData: LayoutData): Vector3 {
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        
        // Calculate adjusted turns if using spacing
        const totalLength = spacing > 0 ? (this.getTotalCount() - 1) * spacing : 0;
        const circumferencePerTurn = 2 * Math.PI * baseRadius;
        const adjustedTurns = spacing > 0 ? Math.max(0.5, totalLength / circumferencePerTurn) : baseTurns;
        
        // Calculate angle and derivatives for tangent
        const angle = t * adjustedTurns * 2 * Math.PI;
        const dAngle = adjustedTurns * 2 * Math.PI; // dt = 1/(totalCount-1)
        
        // Tangent vector components (dx/dt, dy/dt, dz/dt)
        const dx = -baseRadius * Math.sin(angle) * dAngle;
        const dy = height; // Constant height progression  
        const dz = baseRadius * Math.cos(angle) * dAngle;
        
        return new Vector3(dx, dy, dz).normalize();
    }
    
    /**
     * Get total count for tangent calculation (helper)
     */
    private getTotalCount(): number {
        // This will be passed from the actual call, but we need a fallback
        return 10; // Default fallback
    }
    
    /**
     * Transform spiral coordinates based on axis direction
     */
    private transformByAxis(x: number, y: number, z: number, axis: SpiralAxisType): Vector3 {
        switch (axis) {
            case SpiralAxisType.Y:
                return new Vector3(x, y, z);
            case SpiralAxisType.NegativeY:
                return new Vector3(x, -y, z);
            case SpiralAxisType.X:
                return new Vector3(y, x, z);
            case SpiralAxisType.NegativeX:
                return new Vector3(-y, x, z);
            case SpiralAxisType.Z:
                return new Vector3(x, z, y);
            case SpiralAxisType.NegativeZ:
                return new Vector3(x, z, -y);
            default:
                return new Vector3(x, y, z);
        }
    }
    
    /**
     * Adjust orientation based on spiral axis
     */
    private getAxisBasedOrientation(axis: SpiralAxisType, baseRotation: Vector3): Vector3 {
        switch (axis) {
            case SpiralAxisType.Y:
            case SpiralAxisType.NegativeY:
                return baseRotation;
            case SpiralAxisType.X:
            case SpiralAxisType.NegativeX:
                // When spiral grows along X, rotate the base orientation
                return new Vector3(baseRotation.z, baseRotation.y, baseRotation.x);
            case SpiralAxisType.Z:
            case SpiralAxisType.NegativeZ:
                // When spiral grows along Z, rotate the base orientation
                return new Vector3(baseRotation.y, baseRotation.x, baseRotation.z);
            default:
                return baseRotation;
        }
    }
    
    validateSpiralParameters(layoutData: LayoutData): boolean {
        const radius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        const turns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        
        return radius > 0 && height > 0 && turns > 0 && spacing > 0;
    }
    
    validateParameters(layoutData: LayoutData): boolean {
        return super.validateParameters(layoutData) && this.validateSpiralParameters(layoutData);
    }
    
    // Calculate adaptive spiral length based on object count and spacing
    getAdaptiveSpiralLength(totalCount: number, layoutData: LayoutData): number {
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        if (spacing > 0) {
            return (totalCount - 1) * spacing;
        }
        
        // Fall back to fixed spiral length calculation
        const radius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const height = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        const turns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        
        const circumference = 2 * Math.PI * radius;
        const totalCircumference = circumference * turns;
        return Math.sqrt(totalCircumference * totalCircumference + height * height);
    }
    
    // Calculate optimal spacing between objects along spiral
    getOptimalSpacing(objectCount: number, layoutData: LayoutData): number {
        const spiralLength = this.getAdaptiveSpiralLength(objectCount, layoutData);
        return spiralLength / Math.max(1, objectCount - 1);
    }
    
    // Clone method
    clone(): SpiralLayouter {
        return new SpiralLayouter();
    }
    
    // Factory method
    static create(): SpiralLayouter {
        return new SpiralLayouter();
    }
    
    // ==================== ABSTRACT METHOD IMPLEMENTATIONS ====================
    
    protected calculateOrientationQuaternion(
        position: Vector3,
        index: number,
        totalCount: number,
        layoutData: LayoutData
    ): Quaternion {
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        // Calculate parameter t for this object on the spiral
        const t = index / totalCount; // Parameter from 0 to 1
        
        // ============ PROVEN 1D ORIENTATION LOGIC FROM WORKING DEMO ============
        
        // 1. Calculate tangent vector (curve movement direction) using numerical differentiation
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = this.getSpiralPointAt(t1, layoutData);
        const p2 = this.getSpiralPointAt(t2, layoutData);
        const tangent = new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
        
        // 2. Calculate normal vector (pointing toward spiral center for consistent orientation)
        // For spirals, we use a geometric approach instead of second derivative to avoid flipping
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        
        // Calculate adjusted turns (same logic as in position calculation)
        const adjustedTotal = totalCount;
        const totalLength = adjustedTotal * spacing;
        const circumferencePerTurn = 2 * Math.PI * baseRadius;
        const adjustedTurns = spacing > 0 ? Math.max(0.5, totalLength / circumferencePerTurn) : baseTurns;
        const finalTurns = spacing > 0 ? adjustedTurns : baseTurns;
        
        // Calculate current angle using the same logic as position calculation
        const angle = t * finalTurns * 2 * Math.PI;
        
        // Normal always points toward spiral center (radially inward) - this ensures consistent orientation
        let normal = new Vector3(-Math.cos(angle), 0, -Math.sin(angle));
        normal.normalize();
        
        // 3. Calculate binormal vector (perpendicular to spiral plane)
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        // 4. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            // ALIGNED: Object follows the curve direction (uses normal - curvature direction)
            desiredForward = normal.clone();
        } else {
            // PERPENDICULAR: Object is perpendicular to curve (uses tangent - movement direction)
            desiredForward = tangent.clone();
        }
        
        // 5. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
        } else {
            // PATH UP: Use binormal (perpendicular to spiral plane), negated for correct orientation
            desiredUp = binormal.clone().negate();
        }
        
        // 6. Build orthogonal coordinate system using proven logic
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism (degenerative case)
        const dot = Math.abs(desiredForward.dot(desiredUp));
        if (dot > 0.99) {
            if (upVectorMode === UpVectorMode.WorldUp) {
                // Forward is parallel to world up - use tangent direction as fallback
                desiredForward = tangent.clone().normalize();
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            // WORLD UP: Up must stay (0,1,0), calculate right and forward accordingly
            finalUp = new Vector3(0, 1, 0);
            
            // Check if desired forward is parallel to world up
            const parallelThreshold = 0.99;
            const forwardUpDot = Math.abs(desiredForward.dot(finalUp));
            
            if (forwardUpDot > parallelThreshold) {
                // Forward is parallel to world up - use tangent direction as forward
                finalForward = tangent.clone().normalize();
                finalRight = new Vector3().crossVectors(finalUp, finalForward);
                finalRight.normalize();
            } else {
                // Normal case: forward is not parallel to up
                finalRight = new Vector3().crossVectors(finalUp, desiredForward);
                finalRight.normalize();
                
                // Recalculate forward to ensure perfect orthogonality
                finalForward = new Vector3().crossVectors(finalRight, finalUp);
                finalForward.normalize();
            }
        } else {
            // PATH UP: Use the normal approach with binormal as up
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            // Calculate right vector: right = up × forward
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            
            // Recalculate up to ensure orthogonality: up = forward × right
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        // 7. Create quaternion from orthogonal coordinate system
        return Quaternion.lookDir(finalForward, finalUp);
    }
    
    /**
     * Calculate Frenet frame for spiral curve
     * This implements proper 1D curve mathematics from the documentation
     */
    /**
     * Helper method: Get spiral position at parameter t
     */
    private getSpiralPointAt(t: number, layoutData: LayoutData): Vector3 {
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const baseHeight = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const axis = this.getParameter(layoutData, 'spiralAxis', SpiralAxisType.Y);
        
        const angle = t * baseTurns * 2 * Math.PI;
        const spiralRadius = baseRadius;
        const spiralX = Math.cos(angle) * spiralRadius;
        const spiralY = t * baseHeight;
        const spiralZ = Math.sin(angle) * spiralRadius;
        
        return this.transformByAxis(spiralX, spiralY, spiralZ, axis);
    }
    
    /**
     * Helper method: Get tangent vector at parameter t using numerical differentiation
     */
    private getSpiralTangentAt(t: number, layoutData: LayoutData): Vector3 {
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = this.getSpiralPointAt(t1, layoutData);
        const p2 = this.getSpiralPointAt(t2, layoutData);
        return new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
    }

    private calculateSpiralFrenetFrame(t: number, layoutData: LayoutData): {
        tangent: Vector3,
        normal: Vector3,
        binormal: Vector3
    } {
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const baseHeight = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        
        // Calculate spiral parameters
        const angle = t * baseTurns * 2 * Math.PI;
        const spiralRadius = baseRadius * (1 - t * 0.1); // Slight taper
        
        // Tangent vector: derivative of spiral position
        // For spiral: x = r*cos(θ), z = r*sin(θ), y = t*height
        const dAngle = baseTurns * 2 * Math.PI; // dθ/dt
        const dRadius = -baseRadius * 0.1; // dr/dt (taper rate)
        
        const tangent = new Vector3(
            dRadius * Math.cos(angle) - spiralRadius * Math.sin(angle) * dAngle,
            baseHeight, // Constant height progression
            dRadius * Math.sin(angle) + spiralRadius * Math.cos(angle) * dAngle
        ).normalize();
        
        // Normal vector: approximate curvature direction
        // For spiral, this is complex - use simplified approach pointing toward spiral axis
        const normal = new Vector3(
            -Math.cos(angle), // Points toward center
            0,                // No Y component for horizontal spiral
            -Math.sin(angle)  // Points toward center
        ).normalize();
        
        // Binormal vector: tangent × normal
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        
        return { tangent, normal, binormal };
    }
    
    protected getFixedSpacingParameter(layoutData: LayoutData): number {
        return this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
    }
    
    protected calculateUniformSpacing(layoutData: LayoutData, objectCount: number): number {
        // For spiral, uniform spacing means equal distance along the spiral curve
        const spiralLength = this.getAdaptiveSpiralLength(objectCount, layoutData);
        return spiralLength / Math.max(1, objectCount - 1);
    }

    /**
     * Create debug visualization for the spiral path
     * MATCHES THE EXACT LOGIC used in calculatePosition()
     */
    public createDebugVisualization(layoutData: LayoutData): THREE.Group | null {
        // Fallback method - use estimated object count
        return this.createDebugVisualizationWithObjectCount(layoutData, 8);
    }

    /**
     * Create debug visualization with REAL object count
     * MATCHES THE EXACT LOGIC used in calculatePosition()
     */
    public createDebugVisualizationWithObjectCount(layoutData: LayoutData, objectCount: number): THREE.Group | null {
        const debugGroup = new THREE.Group();
        debugGroup.name = 'SpiralLayouter_Debug';

        // Get EXACT same parameters as calculatePosition()
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const baseHeight = this.getParameter(layoutData, 'spiralHeight', this.defaultHeight);
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        const axis = this.getParameter(layoutData, 'spiralAxis', SpiralAxisType.Y);
        
        if (!baseTurns || baseTurns <= 0) {
            return null;
        }

        // USE REAL OBJECT COUNT instead of estimated!
        const adjustedTotal = objectCount - 1;
        const totalLength = adjustedTotal * spacing;
        const circumferencePerTurn = 2 * Math.PI * baseRadius;
        
        // EXACT same adaptive logic as calculatePosition()
        const adjustedTurns = Math.max(0.5, totalLength / circumferencePerTurn);
        const adjustedHeight = totalLength / adjustedTurns;
        
        // Use the adjusted parameters or fall back to user-specified ones
        const finalTurns = spacing > 0 ? adjustedTurns : baseTurns;
        const finalHeight = spacing > 0 ? adjustedHeight : baseHeight;

        // Create spiral curve
        const segments = Math.max(64, finalTurns * 16); // More segments for smoother spiral
        const points: THREE.Vector3[] = [];
        
        // Generate spiral points using EXACT same logic as calculatePosition()
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const angle = t * finalTurns * 2 * Math.PI;
            
            // EXACT same calculation as calculatePosition()
            const spiralRadius = baseRadius * (1 - t * 0.1); // Slight taper - SAME!
            const spiralX = Math.cos(angle) * spiralRadius;
            const spiralY = t * finalHeight;
            const spiralZ = Math.sin(angle) * spiralRadius;
            
            // Transform based on axis direction using SAME transformByAxis logic
            const transformed = this.transformByAxis(spiralX, spiralY, spiralZ, axis);
            points.push(new THREE.Vector3(transformed.x, transformed.y, transformed.z));
        }
        
        // Create spiral line with color gradient
        const spiralGeometry = new THREE.BufferGeometry().setFromPoints(points);
        
        // Create color array for gradient effect (green to red)
        const colors = new Float32Array(points.length * 3);
        for (let i = 0; i < points.length; i++) {
            const t = i / (points.length - 1);
            colors[i * 3] = t;        // Red increases
            colors[i * 3 + 1] = 1 - t; // Green decreases  
            colors[i * 3 + 2] = 0.2;   // Slight blue
        }
        spiralGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        
        const spiralMaterial = new THREE.LineBasicMaterial({ 
            vertexColors: true,
            linewidth: 3,
            transparent: true,
            opacity: 0.8
        });
        
        const debugSpiral = new THREE.Line(spiralGeometry, spiralMaterial);
        debugSpiral.name = 'DebugSpiralPath';
        
        // Add center point indicator
        const centerGeometry = new THREE.SphereGeometry(Math.max(baseRadius * 0.1, 0.1));
        const centerMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xffff00,  // Yellow for center
            transparent: true,
            opacity: 0.7
        });
        const centerSphere = new THREE.Mesh(centerGeometry, centerMaterial);
        centerSphere.position.set(0, 0, 0);
        centerSphere.name = 'SpiralCenter';
        
        // Add position markers along the spiral
        const numMarkers = Math.min(12, Math.max(6, Math.floor(finalTurns * 3)));
        for (let i = 0; i < numMarkers; i++) {
            const t = i / (numMarkers - 1);
            const pointIndex = Math.floor(t * (points.length - 1));
            const markerPos = points[pointIndex];
            
            const markerGeometry = new THREE.SphereGeometry(0.1);
            const markerColor = new THREE.Color().setHSL(t * 0.8, 1.0, 0.5); // Color gradient
            const markerMaterial = new THREE.MeshBasicMaterial({ 
                color: markerColor,
                transparent: true,
                opacity: 0.8
            });
            const marker = new THREE.Mesh(markerGeometry, markerMaterial);
            marker.position.copy(markerPos);
            marker.name = `SpiralMarker_${i}`;
            
            debugGroup.add(marker);
        }
        
        debugGroup.add(debugSpiral);
        debugGroup.add(centerSphere);
        
        return debugGroup;
    }

    /**
     * Create an arrow helper
     */
    private createArrow(position: Vector3, direction: Vector3, size: number, color: number): THREE.Group {
        const arrowGroup = new THREE.Group();
        
        // Arrow shaft
        const shaftGeometry = new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(0, 0, size)
        ]);
        const shaftMaterial = new THREE.LineBasicMaterial({ color: color, linewidth: 2 });
        const shaft = new THREE.Line(shaftGeometry, shaftMaterial);
        
        // Arrow head
        const headGeometry = new THREE.ConeGeometry(size * 0.3, size * 0.4, 6);
        const headMaterial = new THREE.MeshBasicMaterial({ color: color });
        const head = new THREE.Mesh(headGeometry, headMaterial);
        head.position.set(0, 0, size);
        head.rotateX(-Math.PI / 2);
        
        arrowGroup.add(shaft);
        arrowGroup.add(head);
        
        // Position and orient the arrow
        arrowGroup.position.set(position.x, position.y, position.z);
        
        // Orient arrow to point in direction
        const quaternion = new THREE.Quaternion().setFromUnitVectors(
            new THREE.Vector3(0, 0, 1),
            new THREE.Vector3(direction.x, direction.y, direction.z)
        );
        arrowGroup.setRotationFromQuaternion(quaternion);
        
        return arrowGroup;
    }

    /**
     * DEBUGGING FUNCTION: Compare orientation results with working demo
     * This function outputs detailed calculation steps to help identify differences
     */
    public debugOrientationCalculation(index: number, totalCount: number, layoutData: LayoutData): void {
        const alignmentMode = this.getParameter(layoutData, 'alignmentMode', AlignmentMode.Aligned);
        const upVectorMode = this.getParameter(layoutData, 'upVectorMode', UpVectorMode.WorldUp);
        
        //console.log(`\n=== SPIRAL ORIENTATION DEBUG - Index ${index}/${totalCount} ===`);
        //console.log(`Alignment: ${alignmentMode === AlignmentMode.Aligned ? 'ALIGNED' : 'PERPENDICULAR'}`);
        //console.log(`Up Vector: ${upVectorMode === UpVectorMode.WorldUp ? 'WORLD_UP' : 'PATH_UP'}`);
        
        // Calculate parameter t for this object on the spiral
        const t = index / totalCount;
        //console.log(`Parameter t: ${t}`);
        
        // 1. Calculate position for reference
        const position = this.calculatePosition(index, totalCount, layoutData);
        //console.log(`Position: (${position.x.toFixed(3)}, ${position.y.toFixed(3)}, ${position.z.toFixed(3)})`);
        
        // 2. Calculate tangent vector
        const dt = 0.001;
        const t1 = Math.max(0, t - dt);
        const t2 = Math.min(1, t + dt);
        const p1 = this.getSpiralPointAt(t1, layoutData);
        const p2 = this.getSpiralPointAt(t2, layoutData);
        const tangent = new Vector3(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z).normalize();
        //console.log(`Tangent: (${tangent.x.toFixed(3)}, ${tangent.y.toFixed(3)}, ${tangent.z.toFixed(3)})`);
        
        // 3. Calculate normal vector (our fixed approach)
        const baseRadius = this.getParameter(layoutData, 'spiralRadius', this.defaultRadius);
        const baseTurns = this.getParameter(layoutData, 'spiralTurns', this.defaultTurns);
        const spacing = this.getParameter(layoutData, 'spiralFixedSpacing', this.defaultSpacing);
        
        // Calculate adjusted turns (same logic as in position calculation)
        const adjustedTotal = totalCount;
        const totalLength = adjustedTotal * spacing;
        const circumferencePerTurn = 2 * Math.PI * baseRadius;
        const adjustedTurns = spacing > 0 ? Math.max(0.5, totalLength / circumferencePerTurn) : baseTurns;
        const finalTurns = spacing > 0 ? adjustedTurns : baseTurns;
        
        // Calculate current angle using the same logic as position calculation
        const angle = t * finalTurns * 2 * Math.PI;
        //console.log(`Angle: ${angle.toFixed(3)} radians (${(angle * 180 / Math.PI).toFixed(1)}°)`);
        
        // Normal always points toward spiral center (radially inward)
        let normal = new Vector3(-Math.cos(angle), 0, -Math.sin(angle));
        normal.normalize();
        //console.log(`Normal: (${normal.x.toFixed(3)}, ${normal.y.toFixed(3)}, ${normal.z.toFixed(3)})`);
        
        // 4. Calculate binormal vector
        const binormal = new Vector3().crossVectors(tangent, normal).normalize();
        //console.log(`Binormal: (${binormal.x.toFixed(3)}, ${binormal.y.toFixed(3)}, ${binormal.z.toFixed(3)})`);
        
        // 5. Determine forward direction based on alignment mode
        let desiredForward: Vector3;
        if (alignmentMode === AlignmentMode.Aligned) {
            desiredForward = normal.clone();
            //console.log(`Forward (ALIGNED = Normal): (${desiredForward.x.toFixed(3)}, ${desiredForward.y.toFixed(3)}, ${desiredForward.z.toFixed(3)})`);
        } else {
            desiredForward = tangent.clone();
            //console.log(`Forward (PERPENDICULAR = Tangent): (${desiredForward.x.toFixed(3)}, ${desiredForward.y.toFixed(3)}, ${desiredForward.z.toFixed(3)})`);
        }
        
        // 6. Determine up direction based on up vector mode
        let desiredUp: Vector3;
        if (upVectorMode === UpVectorMode.WorldUp) {
            desiredUp = new Vector3(0, 1, 0);
            //console.log(`Up (WORLD_UP): (${desiredUp.x.toFixed(3)}, ${desiredUp.y.toFixed(3)}, ${desiredUp.z.toFixed(3)})`);
        } else {
            desiredUp = binormal.clone().negate();
            //console.log(`Up (PATH_UP = -Binormal): (${desiredUp.x.toFixed(3)}, ${desiredUp.y.toFixed(3)}, ${desiredUp.z.toFixed(3)})`);
        }
        
        // 7. Build orthogonal coordinate system
        desiredForward.normalize();
        desiredUp.normalize();
        
        // Check for parallelism
        const dot = Math.abs(desiredForward.dot(desiredUp));
        //console.log(`Parallelism check (dot product): ${dot.toFixed(3)}`);
        
        if (dot > 0.99) {
            //console.log(`WARNING: Vectors are parallel! Using fallback logic.`);
            if (upVectorMode === UpVectorMode.WorldUp) {
                desiredForward = tangent.clone().normalize();
                //console.log(`Fallback Forward (Tangent): (${desiredForward.x.toFixed(3)}, ${desiredForward.y.toFixed(3)}, ${desiredForward.z.toFixed(3)})`);
            }
        }
        
        let finalRight: Vector3, finalUp: Vector3, finalForward: Vector3;
        
        if (upVectorMode === UpVectorMode.WorldUp) {
            //console.log(`Using WORLD_UP coordinate system building...`);
            finalUp = new Vector3(0, 1, 0);
            
            const parallelThreshold = 0.99;
            const forwardUpDot = Math.abs(desiredForward.dot(finalUp));
            //console.log(`Forward-Up dot product: ${forwardUpDot.toFixed(3)}`);
            
            if (forwardUpDot > parallelThreshold) {
                //console.log(`Forward parallel to world up - using tangent fallback`);
                finalForward = tangent.clone().normalize();
                finalRight = new Vector3().crossVectors(finalUp, finalForward);
                finalRight.normalize();
            } else {
                //console.log(`Normal case - calculating right and forward`);
                finalRight = new Vector3().crossVectors(finalUp, desiredForward);
                finalRight.normalize();
                finalForward = new Vector3().crossVectors(finalRight, finalUp);
                finalForward.normalize();
            }
        } else {
            //console.log(`Using PATH_UP coordinate system building...`);
            finalForward = desiredForward.clone().normalize();
            finalUp = desiredUp.clone().normalize();
            
            finalRight = new Vector3().crossVectors(finalUp, finalForward);
            finalRight.normalize();
            finalUp = new Vector3().crossVectors(finalForward, finalRight);
            finalUp.normalize();
        }
        
        //console.log(`Final Right: (${finalRight.x.toFixed(3)}, ${finalRight.y.toFixed(3)}, ${finalRight.z.toFixed(3)})`);
        //console.log(`Final Up: (${finalUp.x.toFixed(3)}, ${finalUp.y.toFixed(3)}, ${finalUp.z.toFixed(3)})`);
        //console.log(`Final Forward: (${finalForward.x.toFixed(3)}, ${finalForward.y.toFixed(3)}, ${finalForward.z.toFixed(3)})`);
        
        // 8. Calculate final Euler angles
        const matrix = new THREE.Matrix4();
        matrix.makeBasis(
            new THREE.Vector3(finalRight.x, finalRight.y, finalRight.z),
            new THREE.Vector3(finalUp.x, finalUp.y, finalUp.z),
            new THREE.Vector3(finalForward.x, finalForward.y, finalForward.z)
        );
        
        const euler = new THREE.Euler();
        euler.setFromRotationMatrix(matrix, 'XYZ');
        
        const finalRotation = new Vector3(
            euler.x * 180 / Math.PI,
            euler.y * 180 / Math.PI,
            euler.z * 180 / Math.PI
        );
        
        //console.log(`Final Euler Angles (degrees): (${finalRotation.x.toFixed(1)}, ${finalRotation.y.toFixed(1)}, ${finalRotation.z.toFixed(1)})`);
        //console.log(`=== END DEBUG ===\n`);
    }
} 